import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { leaseAPI, paymentAPI, maintenanceAPI, unitAPI, propertyAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import TenantOnboardingWizard from '../../components/TenantOnboardingWizard';
import {
  FileText,
  CreditCard,
  Wrench,
  Home,
  Calendar,
  DollarSign,
  AlertCircle,
  ArrowRight,
  Image,
} from 'lucide-react';
import { useI18n } from '../../context/I18nContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TenantDashboard = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const [lease, setLease] = useState(null);
  const [unit, setUnit] = useState(null);
  const [property, setProperty] = useState(null);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [openTickets, setOpenTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [leasesRes, paymentsRes, ticketsRes, unitsRes] = await Promise.all([
        leaseAPI.getAll(),
        paymentAPI.getAll(),
        maintenanceAPI.getAll(),
        unitAPI.getAll()
      ]);

      const activeLeases = leasesRes.data.filter(l => l.status === 'ACTIVE');
      if (activeLeases.length > 0) {
        setLease(activeLeases[0]);
        const leaseUnit = unitsRes.data.find(u => u.id === activeLeases[0].unit_id);
        setUnit(leaseUnit);
        // Fetch property for address
        if (leaseUnit?.property_id) {
          try {
            const propRes = await propertyAPI.getById(leaseUnit.property_id);
            setProperty(propRes.data);
          } catch {}
        }
      }

      const pending = paymentsRes.data.filter(p => p.status === 'PENDING' || p.status === 'OVERDUE');
      setPendingPayments(pending);

      const open = ticketsRes.data.filter(t => t.status !== 'CLOSED' && t.status !== 'RESOLVED');
      setOpenTickets(open);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Show onboarding wizard on first visit
  useEffect(() => {
    if (!loading && user?.id) {
      const key = `pv_onboarded_${user.id}`;
      if (!localStorage.getItem(key)) {
        setShowWizard(true);
      }
    }
  }, [loading, user]);

  const dismissWizard = () => {
    if (user?.id) localStorage.setItem(`pv_onboarded_${user.id}`, '1');
    setShowWizard(false);
  };

  const quickActions = [
    {
      title: 'View Lease',
      description: 'Access your lease agreement',
      icon: FileText,
      link: '/tenant/lease',
      color: 'text-blue-400'
    },
    {
      title: 'Pay Rent',
      description: 'Make a payment',
      icon: CreditCard,
      link: '/tenant/payments',
      color: 'text-gold',
      badge: pendingPayments.length > 0 ? pendingPayments.length : null
    },
    {
      title: 'Maintenance',
      description: 'Submit or track requests',
      icon: Wrench,
      link: '/tenant/maintenance',
      color: 'text-purple-400',
      badge: openTickets.length > 0 ? openTickets.length : null
    }
  ];

  if (loading) {
    return (
      <DashboardLayout type="tenant" title={t('tenant.dashboard')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-32 bg-card animate-pulse" />
          ))}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout type="tenant" title={t('tenant.dashboard')}>
      {showWizard && (
        <TenantOnboardingWizard
          unit={unit}
          lease={lease}
          property={property}
          onComplete={dismissWizard}
          onDismiss={dismissWizard}
        />
      )}
      <div className="space-y-8" data-testid="tenant-dashboard">
        {/* Welcome */}
        <div>
          <h2 className="font-serif text-2xl mb-1">
            Welcome, <span className="text-gold">{user?.name?.split(' ')[0]}</span>
          </h2>
          <p className="text-muted-foreground">Here's an overview of your tenancy</p>
        </div>

        {/* Unit Info */}
        {unit && (
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center gap-6">
                <div className="w-16 h-16 bg-gold/10 flex items-center justify-center">
                  <Home className="w-8 h-8 text-gold" />
                </div>
                <div>
                  <h3 className="font-serif text-xl">Unit {unit.unit_number}</h3>
                  <p className="text-muted-foreground">
                    {unit.bedrooms === 0 ? 'Studio' : `${unit.bedrooms} Bedroom`} • {unit.bathrooms} Bath
                    {unit.square_feet && ` • ${unit.square_feet} sqft`}
                  </p>
                </div>
                <div className="ml-auto text-right">
                  <p className="text-sm text-muted-foreground">Monthly Rent</p>
                  <p className="text-2xl font-semibold text-gold">${unit.rent_amount?.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Property & Unit Images */}
        {((property?.images?.length > 0) || (unit?.images?.length > 0)) && (
          <Card className="bg-card border-border" data-testid="tenant-images-section">
            <CardContent className="p-6">
              <h3 className="font-serif text-lg mb-4 flex items-center gap-2">
                <Image className="w-5 h-5 text-gold" />
                Your Property
                {property && <span className="text-muted-foreground text-sm font-normal ml-2">{property.name}</span>}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {(property?.images || []).slice(0, 3).map(img => (
                  <div key={img.id} className="aspect-[4/3] bg-background overflow-hidden">
                    <img src={`${API_URL}${img.url}`} alt={img.filename} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                  </div>
                ))}
                {(unit?.images || []).slice(0, 2).map(img => (
                  <div key={img.id} className="aspect-[4/3] bg-background overflow-hidden">
                    <img src={`${API_URL}${img.url}`} alt={img.filename} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {quickActions.map((action, index) => (
            <Link key={action.title} to={action.link}>
              <Card 
                className="bg-card border-border hover:border-gold/30 transition-all duration-300 cursor-pointer card-animate h-full"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`quick-action-${action.title.toLowerCase().replace(' ', '-')}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <action.icon className={`w-8 h-8 ${action.color}`} />
                    {action.badge && (
                      <span className="px-2 py-0.5 bg-gold text-white text-xs font-bold">
                        {action.badge}
                      </span>
                    )}
                  </div>
                  <h3 className="font-medium mb-1">{action.title}</h3>
                  <p className="text-sm text-muted-foreground">{action.description}</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Pending Payments Alert */}
        {pendingPayments.length > 0 && (
          <Card className="bg-amber-500/10 border-amber-500/30">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <AlertCircle className="w-8 h-8 text-amber-400" />
                <div className="flex-1">
                  <h3 className="font-medium text-amber-400">
                    {pendingPayments.length} Pending Payment{pendingPayments.length > 1 ? 's' : ''}
                  </h3>
                  <p className="text-sm text-amber-400/80">
                    Total: ${pendingPayments.reduce((sum, p) => sum + p.amount + (p.late_fee || 0), 0).toLocaleString()}
                  </p>
                </div>
                <Link 
                  to="/tenant/payments" 
                  className="flex items-center gap-1 text-amber-400 hover:underline"
                >
                  Pay Now <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Lease Summary */}
        {lease && (
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-serif text-lg">Lease Summary</h3>
                <Link to="/tenant/lease" className="text-gold text-sm flex items-center gap-1 hover:underline">
                  View Details <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Start Date</p>
                  <p className="font-medium">{lease.start_date}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">End Date</p>
                  <p className="font-medium">{lease.end_date}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Monthly Rent</p>
                  <p className="font-medium text-gold">${lease.monthly_rent?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Status</p>
                  <span className="px-2 py-0.5 text-xs uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                    {lease.status}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* No Lease */}
        {!lease && (
          <Card className="bg-card border-border">
            <CardContent className="py-16 text-center">
              <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-xl font-serif mb-2">No Active Lease</h3>
              <p className="text-muted-foreground">
                You don't have an active lease. Contact your landlord for more information.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default TenantDashboard;
